import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { FilePdf, LinkSimple, FileText, DownloadSimple } from "@phosphor-icons/react";

// Inline viewer for PDF + notes (per requirements). Fetches PDF as blob for the iframe.
export default function Viewer({ entry }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let revoke;
    setBlobUrl(null); setErr("");
    if (entry?.has_file) {
      setLoading(true);
      api.get(`/entries/${entry.id}/file`, { responseType: "blob" })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          revoke = url;
          setBlobUrl(url);
        })
        .catch(() => setErr("Could not load file."))
        .finally(() => setLoading(false));
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [entry?.id, entry?.has_file]);

  if (!entry) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500">
        <FileText size={48} weight="thin" />
        <p className="mt-3 text-sm">Select an entry to inspect</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="viewer">
      <div className="px-6 py-4 border-b border-white/5">
        <h2 className="text-lg font-semibold tracking-tight text-white" data-testid="viewer-title">{entry.title}</h2>
        <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-slate-400 font-mono">
          {entry.url && (
            <a href={entry.url} target="_blank" rel="noreferrer" data-testid="viewer-url" className="flex items-center gap-1 text-blue-400 hover:text-blue-300 truncate max-w-md">
              <LinkSimple size={14} /> {entry.url}
            </a>
          )}
          {entry.file_name && <span className="flex items-center gap-1"><FilePdf size={14} /> {entry.file_name}</span>}
        </div>
      </div>
      <div className="flex-1 overflow-hidden bg-black/20 relative p-4">
        {entry.has_file ? (
          <div className="h-full rounded-md border border-white/10 overflow-hidden relative">
            {loading && <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">Loading document…</div>}
            {err && <div className="absolute inset-0 flex items-center justify-center text-red-400 text-sm">{err}</div>}
            {blobUrl && <iframe title="document" data-testid="viewer-iframe" src={blobUrl} className="w-full h-full bg-white" />}
            {blobUrl && (
              <a href={blobUrl} download={entry.file_name} data-testid="viewer-download"
                className="absolute top-3 right-3 flex items-center gap-1 text-xs bg-[#131722]/90 border border-white/10 rounded-full px-3 py-1.5 text-slate-200 hover:text-white transition-colors duration-200">
                <DownloadSimple size={14} /> Download
              </a>
            )}
          </div>
        ) : (
          <div className="h-full rounded-md border border-dashed border-white/10 flex flex-col items-center justify-center text-slate-500">
            <FileText size={40} weight="thin" />
            <p className="mt-2 text-sm">No PDF attached — notes only</p>
          </div>
        )}
      </div>
      {entry.notes && (
        <div className="border-t border-white/5 px-6 py-4 max-h-40 overflow-y-auto">
          <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-2">Collector Notes</p>
          <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap" data-testid="viewer-notes">{entry.notes}</p>
        </div>
      )}
    </div>
  );
}
